<script setup lang="ts">
/**
 * `Button.vue` — text button covering Unity's `ButtonGrey`,
 * `ButtonWhiteOpaque`, `ButtonWhiteTransparent`, and `ButtonRed` prefabs via
 * the `variant` prop. Keep prop API minimal so the prefab-to-component map
 * stays 1:1 (`<Button variant="grey">` ↔ `ButtonGrey.prefab`).
 */
import Icon from "./Icon.vue";

defineOptions({ name: "Button" });
withDefaults(
  defineProps<{
    label?: string;
    variant?: "grey" | "white" | "white-transparent" | "accent" | "red";
    icon?: string;
    iconAfter?: string;
    disabled?: boolean;
    fullWidth?: boolean;
    size?: "sm" | "md";
  }>(),
  { variant: "grey", size: "md" },
);
defineEmits<{ (e: "click", value: MouseEvent): void }>();
</script>

<template>
  <button
    type="button"
    class="cf-button"
    :class="[
      `cf-button--${variant}`,
      `cf-button--${size}`,
      { 'cf-button--full': fullWidth },
    ]"
    :disabled="disabled"
    @click="$emit('click', $event)"
  >
    <Icon v-if="icon" :name="icon" :size="size === 'sm' ? 14 : 16" />
    <span v-if="label || $slots.default" class="cf-button__label">
      <slot>{{ label }}</slot>
    </span>
    <Icon v-if="iconAfter" :name="iconAfter" :size="size === 'sm' ? 14 : 16" />
  </button>
</template>

<style scoped>
.cf-button {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--cf-space-3);
  padding: 0 var(--cf-space-6);
  border-radius: var(--cf-button-radius);
  font-weight: var(--cf-font-weight-medium);
  white-space: nowrap;
  transition: background var(--cf-motion-fast) var(--cf-ease-default),
    color var(--cf-motion-fast) var(--cf-ease-default);
}

.cf-button--md {
  height: 32px;
  font-size: var(--cf-font-size-base);
}

.cf-button--sm {
  height: var(--cf-row-height-sm);
  font-size: var(--cf-font-size-sm);
  padding: 0 var(--cf-space-4);
}

.cf-button--full {
  width: 100%;
}

.cf-button--grey {
  background: var(--cf-color-button-grey-bg);
  color: var(--cf-color-text);
  box-shadow: var(--cf-shadow-button);
}
.cf-button--grey:hover:not(:disabled) {
  background: var(--cf-color-button-grey-bg-hover);
}
.cf-button--grey:active:not(:disabled) {
  background: var(--cf-color-button-grey-bg-press);
}

.cf-button--white {
  background: var(--cf-color-button-white-bg);
  color: var(--cf-color-button-white-fg);
}
.cf-button--white:hover:not(:disabled) {
  background: var(--cf-color-button-white-bg-hover);
}
.cf-button--white:active:not(:disabled) {
  background: var(--cf-color-button-white-bg-press);
}

.cf-button--white-transparent {
  background: transparent;
  color: var(--cf-color-text);
  border: 1px solid var(--cf-color-panel-divider);
}
.cf-button--white-transparent:hover:not(:disabled) {
  background: var(--cf-color-row-hover);
}

.cf-button--accent {
  background: var(--cf-color-accent);
  color: #1d1d1f;
}
.cf-button--accent:hover:not(:disabled) {
  background: var(--cf-color-accent-hover);
}
.cf-button--accent:active:not(:disabled) {
  background: var(--cf-color-accent-press);
}

/* `ButtonRed.prefab` — the destructive/primary confirm button. */
.cf-button--red {
  background: var(--cf-color-button-red-bg);
  color: #ffffff;
}
.cf-button--red:hover:not(:disabled) {
  background: var(--cf-color-button-red-bg-hover);
}
.cf-button--red:active:not(:disabled) {
  background: var(--cf-color-button-red-bg-press);
}

.cf-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.cf-button__label {
  display: inline-block;
}
</style>
